import { CommonErrors, type HomeDto, type VisitType } from '@lmc/ts-common'
import {
  Add,
  Button,
  Form,
  FormCard,
  FormItem,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Form as AntdForm, Input /* Switch */ } from 'antd'
// import { Text } from '@lmc/ui-kit/src/general'
import { useWatch } from 'antd/lib/form/Form'
import { showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { handleSubmit } from 'screens/Rostering/formComponents/utils'
import type { VisitFormType } from 'screens/Rostering/hooks/useVisitTypesList'
import { getIsDifferentRule, getRequiredRule } from 'screens/Rostering/utils'
import styled from 'styled-components'
import FixedFooter from './FixedFooter'

type VisitsFormProps = {
  types: VisitType[]
  initialFlexiVisits: boolean
  isVisitTypesLoading: boolean
  updateVisitTypes: (values: VisitFormType) => void
  isDirty: boolean
  setIsDirty: (dirty: boolean) => void
}

// const HELP_ARTICLE_URL = 'https://support.logmycare.co.uk'

const VisitsForm = ({
  initialFlexiVisits,
  isVisitTypesLoading,
  updateVisitTypes,
  isDirty,
  setIsDirty
}: VisitsFormProps) => {
  const { t } = useTranslation()

  const [form] = AntdForm.useForm()
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const [flexiVisits /* setFlexiVisits */] =
    useState<boolean>(initialFlexiVisits)

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const initialValues = {
    types: home.rotaSettings?.visitTypes
  }

  useWatch('types', form)

  const handleOnFinish = async (values: VisitFormType) => {
    values.flexiVisits =
      values.flexiVisits !== undefined ? values.flexiVisits : flexiVisits

    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      updateVisitTypes(values)
      setIsUpdateLoading(false)
    } catch (err: any) {
      const errorCode = err?.code

      if (errorCode === CommonErrors.Codes.rotaSettingsTypeInUse) {
        showToast({
          title: t(`YOU_CANNOT_DELETE_THE_ITEM_WHEN_IT_IS_IN_USE`),
          type: 'error'
        })

        const newVisitValue: VisitFormType['types'] = values.types.map(
          (value) => ({
            ...value,
            isDeleted: false
          })
        )

        form.setFieldValue('types', newVisitValue)
      } else {
        showToast({
          title: err?.message || t('SETTINGS_ERROR'),
          type: 'error'
        })
      }
      setIsUpdateLoading(false)
    }
    setIsDirty(false)
  }

  const getDeleteFieldKey = (fieldName: number) => [
    'types',
    fieldName,
    'isDeleted'
  ]

  const deleteHandler = (fieldName: number) => {
    setIsDirty(true)
    const deleteFieldName = getDeleteFieldKey(fieldName)
    form.setFieldValue(deleteFieldName, true)
  }

  const filterDeletedFields = (fieldName: number) => {
    const deleteFieldName = getDeleteFieldKey(fieldName)
    const isDeleted = form.getFieldValue(deleteFieldName)

    return isDeleted !== true
  }

  const handleFormValuesChange = (values: VisitFormType) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const getIsVisitsDifferentRule = (fieldName: number) => {
    const formValues = form.getFieldsValue(true)
    const currentFormValues = formValues.types || []

    return getIsDifferentRule(currentFormValues, fieldName)
  }

  return (
    <FormContainer>
      {isVisitTypesLoading ? (
        <Spinner />
      ) : (
        <Form
          form={form}
          onFinish={handleOnFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          validateTrigger={['']}
        >
          <FormCard title={t('TYPES_VISITS')}>
            <AntdForm.List name="types">
              {(fields, { add }) => (
                <OptionsContainer>
                  {fields
                    .filter((field) => filterDeletedFields(field.name))
                    .map((field) => (
                      <OptionContainer>
                        <FormItem
                          name={[field.name, 'name']}
                          key={['types', field.key].join('-')}
                          rules={[
                            getRequiredRule(t('VISIT_TYPE')),
                            getIsVisitsDifferentRule(field.name)
                          ]}
                        >
                          <Input />
                        </FormItem>
                        {fields?.filter((field) =>
                          filterDeletedFields(field.name)
                        ).length > 1 && (
                          <DeleteButton
                            variant="text"
                            onClick={() => deleteHandler(field.name)}
                            ariaLabel={t('DELETE_ITEM')}
                          >
                            <Trash color={theme.color.neutral.color4} />
                          </DeleteButton>
                        )}
                      </OptionContainer>
                    ))}
                  <Button type="button" variant="text" onClick={() => add()}>
                    <Add />
                    {t('ADD_A_TYPE_OF_VISIT')}
                  </Button>
                </OptionsContainer>
              )}
            </AntdForm.List>
          </FormCard>
          {/* Hide flexi visits for now */}
          {/* <FormCard title={t('FLEXI_VISITS')} helpArticleUrl={HELP_ARTICLE_URL}>
            <ItemContainer>
              {' '}
              <FormItem
                name="flexiVisits"
                label={<Text>{t('FLEXI_VISITS')}</Text>}
              >
                <Switch onChange={setFlexiVisits} checked={flexiVisits} />
              </FormItem>
              <ItemDescription>
                <Text>{t('FLEXI_VISITS_DESCRIPTION')}</Text>
              </ItemDescription>
            </ItemContainer>
          </FormCard> */}
          <FixedFooter
            isUpdateLoading={isUpdateLoading}
            isDirty={isDirty}
            handleSubmit={() => handleSubmit(form, handleOnFinish)}
          />
        </Form>
      )}
    </FormContainer>
  )
}

const FormContainer = styled.div`
  margin-bottom: 50px;
`

const DeleteButton = styled(Button)`
  height: 24px;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 440px;
`

export default VisitsForm
